import { z } from 'zod';
import { isoDate } from './common.js';

/** Links shown to buyers: https only, so a typo can't become a `javascript:` link. */
export const httpsUrl = z
  .string()
  .trim()
  .max(500)
  .url()
  .refine((u) => u.startsWith('https://'), 'https');

/**
 * A link to a page of the event's own site (`/reglement`) or a full https address. A site path
 * starts with one slash (`//host` would leave the site).
 */
export const siteOrHttpsUrl = z
  .string()
  .trim()
  .max(500)
  .refine((u) => /^\/(?!\/)/.test(u) || httpsUrl.safeParse(u).success, 'link');

/** ISO 3166 alpha-2 country code, upper case: where the event takes place (insurer's risk). */
export const countryCode = z
  .string()
  .trim()
  .toUpperCase()
  .regex(/^[A-Z]{2}$/, 'countryCode');

/** Event columns the organiser edits on the settings page (A-13), all nullable. */
const eventSettingsShape = z.object({
  /**
   * Beticketing premium rate in basis points (1700 = 17 %, decision 0025). Null = insurance
   * not offered: the card is hidden at checkout.
   */
  insuranceRateBps: z.number().int().min(1).max(10_000).nullable(),
  /** The insurer's information notice, linked before the opt-in. EN null = the FR one. */
  insuranceTermsUrlFr: httpsUrl.nullable(),
  insuranceTermsUrlEn: httpsUrl.nullable(),
  /** Organiser contact declared to the insurer with every insured ticket (`risk_org*`). */
  insuranceOrgName: z.string().trim().min(1).max(100).nullable(),
  insuranceOrgEmail: z.string().trim().toLowerCase().email().max(90).nullable(),
  /** Country of the event (`risk_countrycode`). */
  countryCode: countryCode.nullable(),
  /** What the competition is (`risk_type`), e.g. "Gravel et trail". */
  insuranceRiskType: z.string().trim().min(1).max(200).nullable(),
  /** The sporting rules accepted at checkout: a site page (`/reglement`) or an https link. */
  rulesUrl: siteOrHttpsUrl.nullable(),
});

/**
 * The settings as saved, with the cross-field rules. Issue messages are i18n key suffixes
 * (`admin.error.<message>`). A rate makes the offer live, so everything the insurer needs is
 * required with it: the notice, the organiser contact, the country and the type of risk.
 */
export const adminEventSettings = eventSettingsShape.superRefine((s, ctx) => {
  if (s.insuranceRateBps === null) return;
  const required = [
    'insuranceTermsUrlFr',
    'insuranceOrgName',
    'insuranceOrgEmail',
    'countryCode',
    'insuranceRiskType',
  ] as const;
  for (const path of required) {
    if (s[path] === null)
      ctx.addIssue({ code: z.ZodIssueCode.custom, path: [path], message: 'requiredWithRate' });
  }
});
export type AdminEventSettings = z.infer<typeof adminEventSettings>;

/** PATCH /admin/event: any subset; the merged result must pass `adminEventSettings`. */
export const updateAdminEventInput = eventSettingsShape.partial().strict();
export type UpdateAdminEventInput = z.infer<typeof updateAdminEventInput>;

export const adminEventOutput = z.object({
  name: z.string(),
  edition: z.string(),
  settings: eventSettingsShape,
  /**
   * Whether checkout offers insurance right now, and if not why: `off` (no rate),
   * `not_configured` (no insurer credentials in this environment), `event_incomplete` (the
   * event has no start date or place), `closed` (the event has started: the risk is closed).
   */
  insuranceOnSale: z.boolean(),
  insuranceBlocker: z.enum(['off', 'not_configured', 'event_incomplete', 'closed']).nullable(),
  /** When the insurer's risk was closed (`lastcall`, at the event start). */
  insuranceRiskClosedAt: isoDate.nullable(),
  /** Insured paid orders not yet declared to the insurer (queued or failing). */
  insurancePendingDeclarations: z.number().int().nonnegative(),
  updatedAt: isoDate,
});
export type AdminEventOutput = z.infer<typeof adminEventOutput>;

/** GET /admin/insurance/statements: the insurer's unpaid premium statements (decision 0025). */
export const adminInsuranceStatements = z.object({
  statements: z.array(
    z.object({
      id: z.number().int(),
      /** The reference to quote when paying. */
      reference: z.string(),
      totalCents: z.number().int(),
      currency: z.string(),
    }),
  ),
});
export type AdminInsuranceStatements = z.infer<typeof adminInsuranceStatements>;
